import { Divider } from '@mui/material';
import {
  contactButtons,
  DigitalChannelType,
  MediaType,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';
import {
  updateAppSpaceTab,
  ContactControlPanelHeading,
  ContactControlPanelSubHeading,
  activeContactSelector,
  getSelectedMenuName,
  globalActions,
  interactionSliceActions,
  setSelectedContact,
  loadContactHistoryData,
  dispositionInteractionActions,
  fetchAgentContactGroup,
  getIsDispositionOpen,
} from '@nice-ccf/ui-components';
import {
  CcfBox,
  CcfCallCommitIcon,
  CcfCallConsultIcon,
  CcfCallHoldIcon,
  CcfCallHungUpIcon,
  CcfCallKeypadIcon,
  CcfCallMaskIcon,
  CcfCallMuteIcon,
  CcfCallRecordIcon,
  CcfCallResloveIcon,
  CcfCallTransferIcon,
  CcfCard,
  CcfCoBrowseIcon,
  CcfConsultIcon,
  CcfLocationIcon,
  CcfTooltip,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import styles from './ccf-contact-control-panel.module.scss';
import { Contact } from '../model/contact';
import React from 'react';
import { Navigation } from '../../../enums/navigation-menus';
import { AppSpace } from '../../../enums/app-space-tabs';
import {
  getContactOutBoundCallStatus,
  setNewCommitment,
  updateActiveContact,
  updateAppSpaceTabIndex,
} from '../../ccf-inbox/ccf-inbox.slice';

/* eslint-disable-next-line */
export interface CcfContactControlPanelProps {
  iconArr: contactButtons[];
  showHeader: boolean;
  showSubHeading: boolean;
  contact: Contact;
  headerText?: string;
  subHeading1?: string;
  subHeading2?: string;
  messageTye?: string;
}
const dialPadIcons = new Map([
  [
    'commit',
    <CcfCallCommitIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfCallCommitIcon>,
  ],
  [
    'mute',
    <CcfCallMuteIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfCallMuteIcon>,
  ],
  [
    'mask',
    <CcfCallMaskIcon
      width="38"
      height="30"
      viewBox="0 0 38 30"
    ></CcfCallMaskIcon>,
  ],
  [
    'hold',
    <CcfCallHoldIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfCallHoldIcon>,
  ],
  [
    'transfer',
    <CcfCallTransferIcon
      className="dailpadOpacity"
      viewBox="0 0 25 20"
    ></CcfCallTransferIcon>,
  ],
  [
    'keypad',
    <CcfCallKeypadIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfCallKeypadIcon>,
  ],
  [
    'record',
    <CcfCallRecordIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfCallRecordIcon>,
  ],
  [
    'consult',
    <CcfCallConsultIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfCallConsultIcon>,
  ],
  [
    'hungup',
    <CcfCallHungUpIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfCallHungUpIcon>,
  ],
  [
    'cobrowsing',
    <CcfCoBrowseIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfCoBrowseIcon>,
  ],
  [
    'location',
    <CcfLocationIcon
      className="dailpadOpacity"
      viewBox="0 0 20 20"
    ></CcfLocationIcon>,
  ],
]);

/**
 * Component to display contact control panel
 * @param props - CcfContactControlPanelProps
 * ```
 * @example-
 * const contactButtonsArray = ['Consult','Commit']
 * const showHeader = true
 * const showSubHeading = false
 * const headerText = 'Liam Davis'
 * const contact = Contact
 * <CcfContactControlPanel iconArr=contactButtonsArray showHeader={showHeader} showSubHeading={showSubHeading} headerText={headerText} contact={Contact}></CcfContactControlPanel>
 * ```
 */
export function CcfContactControlPanel(props: CcfContactControlPanelProps) {
  const dispatch = useDispatch();
  const selectedContact = useSelector(activeContactSelector);
  let controls: contactButtons[];
  const [translate, locale] = useTranslator();
  const lengthGreaterThanEqu4 = props.iconArr.length >= 4 ? true : false;
  const isOutBoundCallInitiated = useSelector(getContactOutBoundCallStatus);

  /*   filtering control button 'hung up' from controls array,
       if more then 4 controls are present hung up is shown adjacent to resolved icon
  */
  if (
    (props.contact.media === MediaType.VOICE ||
      props.contact.media === MediaType.VIDEOCALL) &&
    lengthGreaterThanEqu4
  ) {
    controls = props.iconArr.filter(function (control) {
      return control !== contactButtons.hungup;
    });
  } else {
    controls = props.iconArr;
  }

  const selectedMenuName = useSelector(getSelectedMenuName);
  const { setSelectedMenu } = globalActions;
  const { setDispositionResolution, toggleDispositionCard } =
    dispositionInteractionActions;
  const isDispositionOpen = useSelector(getIsDispositionOpen);

  /**
   * Function to initiate consult for voice contact
   * @example setupForConsult()
   */
  const setupForConsult = () => {
    if (selectedMenuName !== Navigation.INTERACTION) {
      dispatch(setSelectedMenu({ name: Navigation.INTERACTION }));
    }
    dispatch(setSelectedContact(props.contact));
    dispatch(
      loadContactHistoryData({
        activeContact: props.contact,
        start: 0,
        next: 2,
        filterByCategory: 'all',
      })
    );
    //dispatch(setContactTransferStatus({ id: props.contact.id }));
    dispatch(
      updateActiveContact({
        id: props.contact.id || '',
        updates: { setUpForTransfer: true },
      })
    );
    dispatch(updateAppSpaceTab({ tab: AppSpace.DIRECTORY }));
    dispatch(updateAppSpaceTabIndex({ index: 2 })); //Bug fix: navigate to Directory tab in App Space
    dispatch(fetchAgentContactGroup('my_team'));
    // if (
    //   props.contact.contactStatus === 'active' ||
    //   props.contact.contactStatus === 'open'
    // ) {
    //   // dispatch(setInteractionForActiveContact({id : props.contact.id}));
    // } else {
    //   if (props.contact.media === MediaType.DIGITAL) {
    //     // dispatch(setInteractionHeaderTabs({id: props.contact.id , contactStatus: props.contact.contactStatus, contactType:props.contact.channel?.toLowerCase()}));
    //   } else {
    //     // dispatch(setInteractionForActiveContact({id : props.contact.id}));
    //   }
    // }
  };

  /**
   *
   * @param control - contactButtons
   * @param event -React.MouseEvent
   * @example -   controlClicked('Consult', event)
   */
  const controlClicked = (control: contactButtons, event: React.MouseEvent) => {
    switch (control) {
      case contactButtons.transfer:
        if (props.contact.media === MediaType.VOICE) {
          event.stopPropagation();
          setupForConsult();
        }
        if (props.contact.media === MediaType.VIDEOCALL) {
          event.stopPropagation();
          dispatch(updateAppSpaceTab({ tab: AppSpace.COPILOT }));
          // dispatch(setShowAgentsForInteractionTransfer(true));
        }
        if (props.contact.media === MediaType.DIGITAL) {
          event.stopPropagation();
          setupForConsult();
        }

        break;
      case contactButtons.hungup:
        if (selectedContact?.autoSummary !== 'Off') {
          if (!isDispositionOpen) {
            dispatch(toggleDispositionCard(true));
          }
          !isOutBoundCallInitiated
            ? dispatch(
                updateActiveContact({
                  id: props.contact.id || '',
                  updates: { contactStatus: VoiceContactStatus.DISCONNECTED },
                })
              )
            : dispatch(
                updateActiveContact({
                  id: props?.contact?.id || '',
                  updates: { outBoundCallStatus: false },
                })
              );
          dispatch(
            updateActiveContact({
              id: props?.contact?.id || '',
              updates: { outBoundCallStatus: false },
            })
          );
        } else {
          !isOutBoundCallInitiated
            ? dispatch(
                updateActiveContact({
                  id: props.contact.id || '',
                  updates: { contactStatus: VoiceContactStatus.DISCONNECTED },
                })
              )
            : dispatch(
                updateActiveContact({
                  id: props?.contact?.id || '',
                  updates: { outBoundCallStatus: false },
                })
              );
        }

        break;
      case contactButtons.commit:
        event.stopPropagation();
        dispatch(setNewCommitment(true));
        dispatch(updateAppSpaceTab({ tab: AppSpace.SCHEDULE }));
        break;
      default:
        console.log(control + 'is clicked');
        break;
    }
  };

  /**
   * Function gets called on click of mark as resloved button
   * @param event - React.MouseEvent
   * @example  markAsRelovedClicked (event)
   */
  const markAsRelovedClicked = (event: React.MouseEvent) => {
    dispatch(
      setDispositionResolution({
        contactId: props.contact.id,
        isResolved: true,
      })
    );
    // if (selectedContact?.channel === 'CopilotChat') {
    //  // dispatch(setIsCopilotSummarySubmitted(true));
    // }
  };

  return (
    <CcfCard className={styles.controlPanel} square={true}>
      {props.showHeader && (
        <ContactControlPanelHeading
          headerText={
            props.contact?.media !== MediaType.DIGITAL
              ? !(props.contact.isElevated && props.contact.interaction)
                ? translate(props.contact?.media)
                : props.contact.messageType
              : translate(props.contact?.channel)
          }
          headerTextClassess={'controlButtonHeaderText'}
          contact={props.contact}
        />
      )}
      {props.showSubHeading && (
        <ContactControlPanelSubHeading
          contact={props.contact}
          subHeading1={props.subHeading1}
          subHeading2={props.subHeading2}
          subHeading3={''}
        />
      )}
      <CcfBox
        id="ContactControlPanel"
        className={`${
          controls?.length <= 4
            ? 'buttonsGrid'
            : controls?.length === 5
            ? 'buttonsGrid gridPadding5'
            : 'buttonsGrid gridPadding'
        } ${
          props?.messageTye === 'Dialing...' ? styles.disabledControlPanel : ''
        }`}
      >
        {controls.map((item: any) => (
          <CcfTooltip title={translate(item)} arrow>
            <div
              id={`activeContact_${item}Icon`}
              className="digitalPanelButton"
              onClick={(e: React.MouseEvent) => controlClicked(item, e)}
            >
              {dialPadIcons.get(item)}
            </div>
          </CcfTooltip>
        ))}
        {!lengthGreaterThanEqu4 &&
          selectedContact?.messageType !== 'CopilotChat' && (
            <>
              <Divider
                orientation="vertical"
                variant="fullWidth"
                className="verticalDivider"
              />
              <CcfTooltip title={translate('markAsResolved')} arrow>
                <div
                  className={
                    lengthGreaterThanEqu4
                      ? 'markAsResolved'
                      : 'markAsResolved markAsResolvedSmall'
                  }
                  onClick={(e: React.MouseEvent<HTMLElement>) =>
                    markAsRelovedClicked(e)
                  }
                  id={`activeContact_resolvedIcon`}
                >
                  <CcfCallResloveIcon
                    className="resolvedIcon"
                    viewBox="0 0 22 22"
                  ></CcfCallResloveIcon>
                </div>
              </CcfTooltip>
            </>
          )}
      </CcfBox>
      {(lengthGreaterThanEqu4 ||
        selectedContact?.messageType === 'CopilotChat') && (
        <>
          <Divider variant="fullWidth" className="horizontalDivider" />
          <CcfBox
            className={`${'footerContainer'} ${
              lengthGreaterThanEqu4 ? 'fullWidth' : ''
            }`}
          >
            {selectedContact?.messageType === 'CopilotChat' ? (
              <CcfTooltip title={'Consult/Transfer'} arrow>
                <div
                  className="digitalPanelButton hungUp"
                  onClick={(e: React.MouseEvent) =>
                    controlClicked(contactButtons.transfer, e)
                  }
                  id={`activeContact_ConsultIcon`}
                >
                  <CcfConsultIcon
                    className="dailpadOpacity"
                    viewBox="0 0 20 20"
                  ></CcfConsultIcon>
                </div>
              </CcfTooltip>
            ) : (
              <CcfTooltip title={translate('hungup')} arrow>
                <div
                  className="digitalPanelButton hungUp"
                  onClick={(e) => controlClicked(contactButtons.hungup, e)}
                  id={`activeContact_hungUpIcon`}
                >
                  <CcfCallHungUpIcon
                    style={{ fill: '#ff2c2c' }}
                    viewBox="0 0 20 20"
                  ></CcfCallHungUpIcon>
                </div>
              </CcfTooltip>
            )}
            <Divider
              orientation="vertical"
              variant="fullWidth"
              className="verticalDivider"
            />

            <CcfTooltip title={translate('markAsResolved')} arrow>
              <div
                className={
                  lengthGreaterThanEqu4
                    ? 'markAsResolved'
                    : 'markAsResolved markAsResolvedSmall'
                }
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  markAsRelovedClicked(e)
                }
                id={`activeContact_resolvedIcon`}
              >
                <CcfCallResloveIcon
                  className="resolvedIcon"
                  viewBox="0 0 22 22"
                ></CcfCallResloveIcon>
              </div>
            </CcfTooltip>
          </CcfBox>
        </>
      )}
      {!props.contact.isDocked && (
        <Divider variant="fullWidth" className="horizontalDivider" />
      )}
    </CcfCard>
  );
}

export default CcfContactControlPanel;
